/* eslint-disable */
// variant-picker.js
/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();
      this.selectedOptions = this.getSelectedOptions();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      this.addEventListener('change', this.handleVariantChange.bind(this));

      // Initialize custom select dropdowns
      setTimeout(() => {
        this.initializeCustomSelects();
      }, 100);

      // Defer these to allow for quick-add modal to full initialize
      setTimeout(() => {
        this.updateAvailability();
        this.updateAddToCartButton();
      });
    }

    /**
     * Initialize custom select dropdown functionality
     */
    initializeCustomSelects() {
      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const customSelect = selector.querySelector('custom-select');
          if (customSelect && !customSelect.dataset.initialized) {
            this.setupCustomSelect(customSelect);
            customSelect.dataset.initialized = 'true';
          }
        }
      });
    }

    /**
     * Setup custom select dropdown events
     * @param {Element} customSelect - Custom select element
     */
    setupCustomSelect(customSelect) {
      const button = customSelect.querySelector('.custom-select__btn');
      const listbox = customSelect.querySelector('.custom-select__listbox');
      const options = customSelect.querySelectorAll('.custom-select__option');

      if (!button || !listbox) return;

      // Track if we should ignore the next click
      let ignoreNextClick = false;

      // Toggle dropdown on button click
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!ignoreNextClick) {
          this.toggleCustomSelect(customSelect);
        }
        ignoreNextClick = false;
      });

      // Handle option selection with mousedown to capture before blur
      options.forEach(option => {
        option.addEventListener('mousedown', (e) => {
          e.preventDefault();
          e.stopPropagation();
        });
        
        option.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.selectCustomOption(customSelect, option);
          ignoreNextClick = true;
        });
      });

      // Use mousedown on document to capture clicks before the dropdown closes
      document.addEventListener('mousedown', (e) => {
        if (!customSelect.contains(e.target)) {
          this.closeCustomSelect(customSelect);
        }
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && listbox.classList.contains('visible')) {
          this.closeCustomSelect(customSelect);
        }
      });

      // Prevent any interactions from closing the dropdown when it's open
      listbox.addEventListener('mousedown', (e) => {
        e.stopPropagation();
      });
    }

    /**
     * Toggle custom select dropdown
     * @param {Element} customSelect - Custom select element
     */
    toggleCustomSelect(customSelect) {
      const listbox = customSelect.querySelector('.custom-select__listbox');
      
      if (listbox.classList.contains('visible')) {
        this.closeCustomSelect(customSelect);
      } else {
        // Close all other dropdowns first
        this.closeAllCustomSelects();
        this.openCustomSelect(customSelect);
      }
    }

    /**
     * Open custom select dropdown
     * @param {Element} customSelect - Custom select element
     */
    openCustomSelect(customSelect) {
      const listbox = customSelect.querySelector('.custom-select__listbox');
      const button = customSelect.querySelector('.custom-select__btn');
      
      if (!listbox || !button) return;

      listbox.classList.add('visible');
      button.setAttribute('aria-expanded', 'true');
      listbox.removeAttribute('aria-hidden');
      
      // Add styles to ensure dropdown stays on top
      listbox.style.zIndex = '1000';
      listbox.style.pointerEvents = 'auto';
      
      this.addBackdrop();
    }

    /**
     * Close custom select dropdown
     * @param {Element} customSelect - Custom select element
     */
    closeCustomSelect(customSelect) {
      const listbox = customSelect.querySelector('.custom-select__listbox');
      const button = customSelect.querySelector('.custom-select__btn');
      
      if (!listbox || !button) return;

      listbox.classList.remove('visible');
      button.setAttribute('aria-expanded', 'false');
      listbox.setAttribute('aria-hidden', 'true');
      
      this.removeBackdrop();
    }

    /**
     * Close all custom select dropdowns
     */
    closeAllCustomSelects() {
      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          const customSelect = selector.querySelector('custom-select');
          if (customSelect) {
            this.closeCustomSelect(customSelect);
          }
        }
      });
    }

    /**
     * Select an option in custom select
     * @param {Element} customSelect - Custom select element
     * @param {Element} selectedOption - Selected option element
     */
    selectCustomOption(customSelect, selectedOption) {
      const options = customSelect.querySelectorAll('.custom-select__option');
      const button = customSelect.querySelector('.custom-select__btn');
      const valueText = selectedOption.querySelector('.js-value')?.textContent || selectedOption.textContent;
      const value = selectedOption.getAttribute('data-value');

      // Update selected state
      options.forEach(option => option.classList.remove('is-selected'));
      selectedOption.classList.add('is-selected');

      // Update button text
      if (button) {
        button.querySelector('span').textContent = valueText;
        
        // Update data attributes if needed
        if (selectedOption.hasAttribute('data-swatch')) {
          button.setAttribute('data-swatch', selectedOption.getAttribute('data-swatch'));
        }
      }

      // Close dropdown immediately
      this.closeCustomSelect(customSelect);
      
      // Trigger change event after a tiny delay to ensure DOM updates
      setTimeout(() => {
        const changeEvent = new Event('change', { bubbles: true });
        customSelect.dispatchEvent(changeEvent);
      }, 10);
    }

    /**
     * Add backdrop to prevent body scrolling
     */
    addBackdrop() {
      if (!this.backdrop) {
        this.backdrop = document.createElement('div');
        this.backdrop.className = 'custom-select-backdrop';
        this.backdrop.style.cssText = `
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 999;
          background: transparent;
          cursor: default;
        `;
        document.body.appendChild(this.backdrop);
        
        // Use mousedown instead of click to capture before other events
        this.backdrop.addEventListener('mousedown', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.closeAllCustomSelects();
        });
      }
    }

    /**
     * Remove backdrop
     */
    removeBackdrop() {
      if (this.backdrop) {
        this.backdrop.remove();
        this.backdrop = null;
      }
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.selectedOptions = this.getSelectedOptions();
      this.variant = this.getSelectedVariant();
      this.preSelection = !this.variant && this.selectedOptions.find((o) => o === null) === null;

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      }

      this.updateAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.updateMetafieldVisibility();
      VariantPicker.updateLabelText(evt);

      if (!this.preSelection) {
        this.dispatchEvent(new CustomEvent('on:variant:change', {
          bubbles: true,
          detail: {
            form: this.productForm,
            variant: this.variant,
            product: this.data.product
          }
        }));
      }
    }

    /**
     * Shows/hides variant metafield elements on the page as necessary
     */
    updateMetafieldVisibility() {
      document.querySelectorAll('[data-variant-metafield]').forEach((elem) => {
        elem.classList.toggle('hidden', elem.dataset.variantMetafield !== this.variant?.id.toString());
      });
    }

    /**
     * Updates the "Add to Cart" button label and disabled state.
     */
    updateAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.addBtn || this.productForm.querySelector('[name="add"]');
      const variantAvailable = this.variant && this.variant.available;

      this.addBtn.disabled = !variantAvailable || this.preSelection;

      if (this.preSelection) {
        this.addBtn.textContent = theme.strings.noSelectedVariant;
      } else {

        const unavailableStr = this.variant ? theme.strings.noStock : theme.strings.noVariant;
        this.addBtn.textContent = variantAvailable
          ? this.addBtn.dataset.addToCartText
          : unavailableStr;
      }
    }

    /**
     * Updates the availability status of an option.
     * @param {Element} optionEl - Option element.
     * @param {boolean} exists - Does this option lead to a variant that exists?
     * @param {boolean} available - Does this option lead to a variant that is available to buy?
     */
    static updateOptionAvailability(optionEl, exists, available) {
      const el = optionEl;
      const unavailableText = exists ? theme.strings.noStock : theme.strings.noVariant;
      el.classList.toggle('is-unavailable', !available);
      el.classList.toggle('is-nonexistent', !exists);

      if (optionEl.classList.contains('custom-select__option')) {
        const em = el.querySelector('em');

        if (em) {
          em.hidden = available;
        }

        if (!available) {
          if (em) {
            em.textContent = unavailableText;
          } else {
            el.innerHTML = `${el.innerHTML} <em class="pointer-events-none">${unavailableText}</em>`;
          }
        }
      } else if (!available) {
        el.nextElementSibling.title = unavailableText;
      } else {
        el.nextElementSibling.removeAttribute('title');
      }
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      const { availabilityMethod } = this.dataset; // 'downward' or 'selection'
      let currVariant = this.variant;

      if (!this.variant) {
        currVariant = { options: this.selectedOptions };
      }

      if (availabilityMethod === 'selection') {
        // Flag all options as unavailable
        this.querySelectorAll('.js-option').forEach((optionEl) => {
          VariantPicker.updateOptionAvailability(optionEl, false, false);
        });

        // Flag selector options as available or sold out, depending on the variant availability
        this.optionSelectors.forEach((selector, selectorIndex) => {
          this.data.product.variants.forEach((variant) => {
            let matchCount = 0;

            variant.options.forEach((option, optionIndex) => {
              if (option === currVariant.options[optionIndex] && optionIndex !== selectorIndex) {
                matchCount += 1;
              }
            });

            if (matchCount === currVariant.options.length - 1) {
              const options = selector.querySelectorAll('.js-option');
              const optionEl = Array.from(options).find((opt) => {
                if (selector.dataset.selectorType === 'dropdown') {
                  return opt.dataset.value === variant.options[selectorIndex];
                }
                return opt.value === variant.options[selectorIndex];
              });

              if (optionEl) {
                VariantPicker.updateOptionAvailability(optionEl, true, variant.available);
              }
            }
          });
        });
      } else {
        this.optionSelectors.forEach((selector, selectorIndex) => {
          const options = selector.querySelectorAll('.js-option:not([data-value=""])');
          options.forEach((option) => {
            const optionValue = selector.dataset.selectorType === 'dropdown' ? option.dataset.value : option.value;
            // any available variants with previous options and this one locked in?
            let variantsExist = false;
            let variantsAvailable = false;
            this.data.product.variants.forEach((v) => {
              let matches = 0;
              for (let i = 0; i < selectorIndex; i += 1) {
                if (v.options[i] === this.selectedOptions[i] || this.selectedOptions[i] === null) {
                  matches += 1;
                }
              }
              if (v.options[selectorIndex] === optionValue && matches === selectorIndex) {
                variantsExist = true;
                if (v.available) {
                  variantsAvailable = true;
                }
              }
            });
            VariantPicker.updateOptionAvailability(option, variantsExist, variantsAvailable);
          });
        });
      }
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the color option label text.
     * @param {object} evt - Event object
     */
    static updateLabelText(evt) {
      const selector = evt.target.closest('.option-selector');
      if (selector.dataset.selectorType === 'dropdown') return;

      const colorText = selector.querySelector('.js-color-text');
      if (!colorText) return;

      colorText.textContent = evt.target.nextElementSibling.querySelector('.js-value').textContent;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }

    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      let displayVariant = this.variant;
      
      // Handle pre-selection state (no variant selected yet)
      if (this.preSelection || !this.variant) {
        // Find the first available variant or cheapest variant for price range display
        displayVariant = this.data.product.variants.find(v => v.available) || this.data.product.variants[0];
        
        // Show "from" price when no variant is selected
        const fromEl = this.price.querySelector('.price__from');
        if (fromEl) {
          fromEl.hidden = false;
        }
      }

      if (displayVariant) {
        const variantData = this.data.formatted[displayVariant.id];
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        // Update current price
        if (priceCurrentEl) {
          priceCurrentEl.innerHTML = variantData.price;
        }

        // Update compare at price if on sale
        if (priceWasEl && variantData.compareAtPrice) {
          priceWasEl.innerHTML = variantData.compareAtPrice;
          priceWasEl.hidden = false;
        } else if (priceWasEl) {
          priceWasEl.hidden = true;
        }

        // Update unit price
        if (unitPriceEl && displayVariant.unit_price_measurement) {
          const valueEl = unitPriceEl.querySelector('.unit-price__price');
          const unitEl = unitPriceEl.querySelector('.unit-price__unit');
          
          if (valueEl) {
            valueEl.innerHTML = variantData.unitPrice;
          }
          if (unitEl) {
            const value = displayVariant.unit_price_measurement.reference_value;
            const unit = displayVariant.unit_price_measurement.reference_unit;
            unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          }
          unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        // Update price styling
        this.price.classList.toggle(
          'price--on-sale',
          displayVariant.compare_at_price > displayVariant.price
        );
        this.price.classList.toggle('price--sold-out', !displayVariant.available && !this.preSelection);
      }

      // Show/hide appropriate price elements
      const priceDefaultEl = this.price.querySelector('.price__default');
      const priceNoVariantEl = this.price.querySelector('.price__no-variant');
      
      if (priceDefaultEl) {
        priceDefaultEl.hidden = !this.variant && !this.preSelection;
      }
      if (priceNoVariantEl) {
        priceNoVariantEl.hidden = this.variant || this.preSelection;
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          // For custom select dropdowns
          const customSelect = selector.querySelector('custom-select');
          if (customSelect) {
            const selectedOption = customSelect.querySelector('.custom-select__option.is-selected');
            if (selectedOption) {
              selectedOptions.push(selectedOption.getAttribute('data-value'));
            } else {
              // Fallback to button text
              const buttonText = customSelect.querySelector('.custom-select__btn span').textContent.trim();
              selectedOptions.push(buttonText === this.dataset.placeholderText ? null : buttonText);
            }
          } else {
            // Legacy dropdown support
            const selectedText = selector.querySelector('.custom-select__btn').textContent.trim();
            selectedOptions.push(selectedText === this.dataset.placeholderText ? null : selectedText);
          }
        } else {
          // For radio button selectors
          const selected = selector.querySelector('input:checked');
          selectedOptions.push(selected ? selected.value : null);
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();
      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }
  }

  customElements.define('variant-picker', VariantPicker);
}